import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { FloatingWhatsApp } from '@/components/FloatingWhatsApp';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const faqs = [
  {
    question: 'What is Digital Marketing?',
    answer: 'Digital marketing encompasses all marketing efforts that use electronic devices or the internet. It includes online channels such as search engines, social media, email, and websites to connect with current and prospective customers.',
  },
  {
    question: 'Why is Digital Marketing Important?',
    answer: 'Digital marketing allows businesses to reach a global audience, target specific demographics, and measure campaign performance in real-time, often at a lower cost than traditional marketing methods.',
  },
  {
    question: 'How Does SEO Work?',
    answer: 'SEO involves optimizing a website to improve its visibility on search engines. SEO includes keyword research, on-page optimization, building quality backlinks, and providing a positive user experience.',
  },
  {
    question: 'What Platforms Are Best for Social Media Marketing?',
    answer: 'The choice depends on the target audience. Popular platforms include Facebook, Instagram, TikTok, Twitter, LinkedIn, and Pinterest. Each platform has its own demographic and content style.',
  },
  {
    question: 'How Can Content Marketing Benefit My Business?',
    answer: 'Content marketing builds brand authority, engages your audience, and helps with SEO. You can attract and retain a clearly defined audience by providing valuable and relevant content.',
  },
  {
    question: 'What is Email Marketing and Why Is It Effective?',
    answer: 'Email marketing involves sending targeted messages to a group of people through email. This method is effective for direct communication with the audience, personalized messaging, and cost-effectiveness.',
  },
  {
    question: 'What is PPC Advertising?',
    answer: 'Pay-per-click advertising is a popular digital advertising model that allows advertisers to reach their target audience by placing their ads across various platforms. In this model, advertisers are charged a fee each time a user clicks on their ad. This model is known for its effectiveness in driving website traffic and increasing brand visibility.',
  },
  {
    question: 'How Can Analytics Help in Digital Marketing?',
    answer: 'Analytics tools like Google Analytics provide insights into website traffic, user behavior, and the effectiveness of marketing campaigns. This data helps in making informed decisions and optimizing strategies.',
  },
  {
    question: 'What Trends Are Shaping Digital Marketing?',
    answer: 'Key trends include the rise of video content, increased use of artificial intelligence, voice search optimization, and a focus on user experience.',
  },
];

export const FAQPage = () => {
  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Digital Marketing FAQ | MARKETYZE</title>
        <meta name="description" content="Answers to common questions about digital marketing, SEO, PPC, Google Ads, Analytics, and more from former Google specialists." />
        <link rel="canonical" href="https://marketyze.eu/faq" />
        <meta property="og:title" content="Digital Marketing FAQ | MARKETYZE" />
        <meta property="og:url" content="https://marketyze.eu/faq" />
        <meta name="twitter:title" content="Digital Marketing FAQ | MARKETYZE" />
        <meta name="twitter:description" content="Answers to common questions about digital marketing, SEO, PPC, Google Ads, Analytics, and more from former Google specialists." />
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
      </Helmet>
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back Button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Frequently Asked <span className="text-[#4285F4]">Questions</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Find answers to common questions about digital marketing and our services.
            </p>
          </div>

          {/* FAQ Accordion */}
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-card border border-border/50 rounded-xl px-6 shadow-card data-[state=open]:shadow-card-hover transition-shadow"
              >
                <AccordionTrigger className="text-left font-heading font-semibold text-foreground hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA */}
          <div className="mt-12 text-center p-8 bg-gradient-to-br from-[#4285F4]/10 to-[#34A853]/10 rounded-2xl border border-[#4285F4]/20">
            <h2 className="font-heading text-xl font-semibold text-foreground mb-2">
              Still have questions?
            </h2>
            <p className="text-muted-foreground mb-4">
              We're here to help. Schedule a free consultation.
            </p>
            <Link to="/booking" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <Button className="bg-[#EA4335] hover:bg-[#EA4335]/90 text-white rounded-full px-6">
                Book a Call
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default FAQPage;
